// Item stock trails (legacy ItemTrailComponent): opening/closing stock for the
// period + the stock trail rows (GET items/trails/{id} and /table).
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, MenuItem, Paper, Stack, TableCell, TableRow, TextField, Typography } from '@mui/material';
import { ArrowLeft2 } from 'iconsax-reactjs';

import { adminApi, apiErrorMessage, fetchRecord } from '../../api/client';
import { formatDateTime, t, tLabel } from '../../i18n';
import { ensureLocation } from '../../session/location';
import { RemoteTable, type RemoteColumn } from './RemoteTable';
import { asRecord, metaLabel, useLocations } from './shared';

type Trail = Record<string, unknown>;

function toInput(date: Date) {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function toApi(value: string) {
  return value ? `${value.replace('T', ' ')}:00` : '';
}

function formatQty(value: unknown) {
  const number = Number(value ?? 0);
  return (Number.isFinite(number) ? number : 0).toLocaleString(undefined, { maximumFractionDigits: 4 });
}

export function ItemTrailsPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const locations = useLocations();
  const [item, setItem] = useState<Record<string, unknown> | null>(null);
  const [summary, setSummary] = useState<{ start: number; close: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState(() => {
    const end = new Date();
    end.setHours(23, 59, 0, 0);
    const start = new Date();
    start.setMonth(start.getMonth() - 3);
    start.setHours(0, 0, 0, 0);
    return { start_date: toInput(start), end_date: toInput(end), location_id: '' };
  });
  const [applied, setApplied] = useState(draft);
  const params = useMemo(() => {
    const out: Record<string, string> = { start_date: toApi(applied.start_date), end_date: toApi(applied.end_date) };
    if (applied.location_id) out.location_id = applied.location_id;
    return out;
  }, [applied]);

  useEffect(() => {
    fetchRecord<Record<string, unknown>>('items', id).then(setItem).catch(() => setItem(null));
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await ensureLocation();
        const response = await adminApi.get(`items/trails/${id}`, { params });
        const stock = asRecord(response.data?.stock);
        if (!cancelled) setSummary({ start: Number(stock.start_stock ?? 0), close: Number(stock.close_stock ?? 0) });
      } catch (err) {
        if (!cancelled) setError(apiErrorMessage(err, t('catalog.trails.loadFailed')));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, params]);

  const columns: RemoteColumn<Trail>[] = [
    { key: 'created_at', label: tLabel('Created At'), sortable: true, render: (row) => formatDateTime(row.created_at) },
    { key: 'memo', label: tLabel('Type'), sortable: true, render: (row) => String(row.memo ?? row.type ?? '').replace(/_/g, ' ') },
    { key: 'item', label: tLabel('Item'), render: (row) => String(asRecord(row.item).name ?? item?.name ?? '') },
    { key: 'quantity', label: tLabel('Quantity'), align: 'center', render: (row) => formatQty(row.quantity) },
    { key: 'unit', label: tLabel('Unit'), render: (row) => String(asRecord(row.unit).name ?? '') },
    { key: 'location', label: tLabel('Location'), render: (row) => String(asRecord(row.location).name ?? '') },
    { key: 'variation', label: t('catalog.variation'), render: (row) => { const variation = asRecord(row.variation); return String(variation.code ?? '') || metaLabel(variation.meta); } }
  ];

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h4" fontWeight={700}>{t('catalog.trails.title')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {item ? `${String(item.name ?? '')} (${String(item.code ?? '')})` : ''} — {formatDateTime(params.start_date)} → {formatDateTime(params.end_date)}
            </Typography>
          </Box>
          <Button startIcon={<ArrowLeft2 size={16} />} onClick={() => navigate(-1)}>{t('action.back')}</Button>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }} alignItems={{ md: 'center' }}>
          <TextField size="small" type="datetime-local" label={t('catalog.trails.from')} InputLabelProps={{ shrink: true }} value={draft.start_date} onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} />
          <TextField size="small" type="datetime-local" label={t('catalog.trails.to')} InputLabelProps={{ shrink: true }} value={draft.end_date} onChange={(e) => setDraft({ ...draft, end_date: e.target.value })} />
          <TextField select size="small" label={tLabel('Location')} value={draft.location_id} onChange={(e) => setDraft({ ...draft, location_id: e.target.value })} sx={{ minWidth: 200 }}>
            <MenuItem value="">{t('catalog.allLocations')}</MenuItem>
            {locations.map((location) => <MenuItem key={location.value} value={location.value}>{location.label}</MenuItem>)}
          </TextField>
          <Button variant="contained" onClick={() => setApplied(draft)}>{t('action.apply')}</Button>
        </Stack>
      </Paper>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Paper variant="outlined" sx={{ p: 2, flex: 1, borderRadius: 2 }}>
          <Typography variant="h5" fontWeight={700}>{summary ? formatQty(summary.start) : '—'}</Typography>
          <Typography variant="body2" color="text.secondary">{t('catalog.trails.startStock')}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, flex: 1, borderRadius: 2 }}>
          <Typography variant="h5" fontWeight={700}>{summary ? formatQty(summary.close) : '—'}</Typography>
          <Typography variant="body2" color="text.secondary">{t('catalog.trails.closeStock')}</Typography>
        </Paper>
      </Stack>
      <RemoteTable<Trail>
        endpoint={`items/trails/${id}/table`}
        columns={columns}
        params={params}
        requiresLocation
        rowTone={(row) => (Number(row.quantity) < 0 ? 'warning' : Number(row.quantity) > 0 ? 'success' : undefined)}
        footer={(rows) => (
          <TableRow>
            <TableCell colSpan={3}><strong>{t('catalog.trails.pageTotal')}</strong></TableCell>
            <TableCell align="center"><strong>{formatQty(rows.reduce((sum, row) => sum + Number(row.quantity ?? 0), 0))}</strong></TableCell>
            <TableCell colSpan={3} />
          </TableRow>
        )}
      />
    </Stack>
  );
}
